import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ActiveProductSelectors from 'selectors/ActiveProductSelectors';
import * as UserSelectors from 'selectors/UserSelectors';

import { getLocationUrlById } from 'common/utils/shopUtils';
import { Location } from 'common/types';

import DropDownWithLabel from 'components/DropDownWithLabel';
import { Typography, Theme, Box, SelectChangeEvent } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { getLanguageNativeName } from 'common/locales/utils';
import { getLanguageFlag } from 'common/utils/getLanguageFlag';
import { useTranslation } from 'services/localization/useTranslation';

import CopyableText from 'components/CopyableText';
import { isLiftTicketPackageProduct, isLiftTicketProduct } from 'common/modules/skidata';

const Embed = () => {
	const { getTranslation } = useTranslation();
	const classes = useStyles();
	const { t } = useTranslation();

	const activeProduct = useSelector(ActiveProductSelectors.productData)!;
	const shopLocations = useSelector(ShopSelectors.activeShopAllLocations);
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const activeShopLanguages = useSelector(ShopSelectors.activeShopLanguages);
	const activeShopStoreUrl = useSelector(ShopSelectors.activeShopOnlineURL);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const shopLocationUrls = useSelector(ShopSelectors.activeShopLocationUrls);
	const categoriesLoading = useSelector(StockSelectors.categoriesLoading);
	const categoriesById = useSelector(StockSelectors.categoriesById);

	const locationsVisibleForProduct = activeProduct.visibleInLocations ?? ['ALL'];

	const [_embedCategoryOptions, setEmbedCategoryOptions] = useState<
		{ id: string; label: string }[] | undefined
	>(undefined);
	const [_embedCategoryId, setEmbedCategoryId] = useState<string | undefined>(undefined);
	const [embedLanguage, setEmbedLanguage] = useState<string>(shopDefaultLanguage);
	const [embedLocationId, setEmbedLocationId] = useState<string | undefined>(activeLocationId!);

	const locationVisibleForProduct = (l: Location) =>
		locationsVisibleForProduct.find((locationId) => locationId === l.id || locationId === 'ALL');

	useEffect(() => {
		if (activeProduct.categoryIds) {
			if (activeProduct.categoryIds.length > 0 && !categoriesLoading) {
				const categoryIdsWithNames = activeProduct.categoryIds.map((id) => {
					return {
						id,
						label: getTranslation(categoriesById[id].name),
					};
				});
				setEmbedCategoryOptions(categoryIdsWithNames);
				setEmbedCategoryId(categoryIdsWithNames[0].id);
			} else {
				setEmbedCategoryId('others');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeProduct.categoryIds, categoriesLoading, categoriesById]);

	const handleEmbeddingChange = (event: SelectChangeEvent) => {
		event.target.name === 'language'
			? setEmbedLanguage(event.target.value as string)
			: event.target.name === 'location'
			? setEmbedLocationId(event.target.value as string)
			: event.target.name === 'category' && setEmbedCategoryId(event.target.value as string);
	};

	const getEmbedLocationUrl = (selectedLocationId?: string) => {
		if (selectedLocationId == null || shopLocations.length <= 1) return '';
		const locationUrl = getLocationUrlById(selectedLocationId, shopLocationUrls);
		return locationUrl ? `/l/${locationUrl}` : '';
	};

	const embedLocationOptions = [
		...shopLocations.filter(locationVisibleForProduct).map((l) => ({ id: l.id, label: l.name })),
	];

	const shopLanguageOptions = [
		...activeShopLanguages.map((lang) => ({
			id: lang,
			label: getLanguageNativeName(lang),
			img: getLanguageFlag(lang),
		})),
	];

	const getProductUrl = () => {
		let productPath = '';
		if (isLiftTicketPackageProduct(activeProduct)) {
			productPath = `/packageTicket/${activeProduct.id}`;
		} else if (isLiftTicketProduct(activeProduct)) {
			productPath = '/tickets';
		} else {
			productPath = `/product/${activeProduct.id}`;
		}
		return `${activeShopStoreUrl}${getEmbedLocationUrl(
			embedLocationId,
		)}${productPath}?lang=${embedLanguage}`;
	};

	return embedLocationId ? (
		<>
			<DropDownWithLabel
				title={t('payLinkInfo.language')}
				name={'language'}
				value={embedLanguage}
				options={shopLanguageOptions}
				variant={'outlined'}
				handleSelectChange={handleEmbeddingChange}
				classes={{
					menu: classes.dialogMenuOverwrite,
				}}
			/>
			{embedLocationOptions.length > 1 && (
				<DropDownWithLabel
					title={t('common:shopLocation.location', 'Location')}
					name={'location'}
					value={embedLocationId}
					options={embedLocationOptions}
					variant={'outlined'}
					handleSelectChange={handleEmbeddingChange}
					classes={{
						menu: classes.dialogMenuOverwrite,
					}}
				/>
			)}
			{activeProduct && (
				<>
					<Typography variant="body1" gutterBottom>
						{t('product.url', 'Direct URL')}
					</Typography>
					<CopyableText text={getProductUrl()} />
					<Box mt={2} />
					<Typography variant="body1" gutterBottom>
						{t('product.iframe', 'Embed to website')}
					</Typography>
					<CopyableText
						text={`<iframe width="100%" height="100%" style="border:0;" src="${getProductUrl()}"></iframe>`}
					/>
				</>
			)}
		</>
	) : (
		<Typography>
			{t('shop.notVisibleInAnyLocation', 'Product is not visible in any location')}
		</Typography>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		dialogMenuOverwrite: {
			zIndex: '1302 !important' as any,
		},
	}),
);

export default Embed;
